import React from "react";
import { QUESTIONS } from "../lib/questions";
import { humanizeKey, recommendationSentence } from "../lib/scoring";

const fmt = (v) => humanizeKey(v || "—");
const money = (n) => `$${(n ?? 0).toLocaleString()}`;
const range = (lo, hi) => `${money(lo)} – ${money(hi)}`;

const renderAnswer = (value) => {
  if (Array.isArray(value)) return value.length === 0 ? "—" : value.join(", ");
  if (!value) return "—";
  return value;
};

const Section = ({ title, children, testid }) => (
  <section className="flex flex-col gap-4" data-testid={testid}>
    <span className="gw-caption" style={{ letterSpacing: "0.08em" }}>{title}</span>
    {children}
  </section>
);

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="gw-small" style={{ color: "var(--text-secondary)" }}>{label}</span>
    <span className="gw-body" style={{ color: "var(--text-primary)" }}>{value}</span>
  </div>
);

const ScoreCard = ({ label, val }) => (
  <div className="flex flex-col gap-1 p-3" style={{
    border: "1px solid var(--border-card)", borderRadius: 12, background: "var(--page-secondary)",
  }}>
    <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>{label}</span>
    <span style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
      {val}
    </span>
  </div>
);

const ROADMAP_LABELS = {
  inventory: "Inventory", purchasing: "Purchasing", production_planning: "Production planning",
  costing: "Costing", traceability: "Traceability", integrations: "Integrations",
  reporting: "Reporting", cash_flow_inventory_planning: "Cash flow / inventory planning",
  ai_recommendations: "AI recommendations", copacker_visibility: "Co-packer visibility",
  tool_consolidation: "Tool consolidation",
};

export default function AdminReport({ data }) {
  const roadmap = data.roadmap_priority_scores || {};
  const roadmapEntries = Object.entries(roadmap).sort((a, b) => b[1] - a[1]);
  const maxRoadmap = Math.max(1, ...roadmapEntries.map(([, v]) => v));
  const breakdown = data.system_cost_breakdown || [];
  const notes = data.system_cost_estimation_notes || [];

  return (
    <div className="flex flex-col gap-8">
      {/* 1. Profile overview */}
      <Section title="1 — PROFILE OVERVIEW" testid="report-overview">
        <div className="grid grid-cols-2 gap-3">
          <Row label="SKU range" value={renderAnswer(data.sku_range)} />
          <Row label="Team size" value={renderAnswer(data.employee_count_range)} />
          <Row label="Annual revenue" value={renderAnswer(data.annual_revenue_range)} />
          <Row label="Production cadence" value={renderAnswer(data.production_frequency)} />
        </div>
        <Row label="Current tools" value={renderAnswer(data.current_tools)} />
        <Row label="Current operating methods" value={renderAnswer(data.current_operating_methods)} />
      </Section>

      <hr className="gw-divider" />

      {/* 2. Scores */}
      <Section title="2 — SCORES" testid="report-scores">
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Fit", data.fit_score], ["Pain", data.pain_score],
            ["Company size", data.company_size_score], ["Revenue", data.revenue_score],
            ["Account value", data.account_value_score],
            ["Replacement opportunity", data.replacement_opportunity_score],
            ["Integration urgency", data.integration_urgency_score],
            ["Centralization value", data.centralization_value_score],
            ["Data maturity", data.data_maturity_score],
            ["Automation readiness", data.automation_readiness_score],
            ["AI trust", data.ai_trust_score],
          ].map(([label, val]) => <ScoreCard key={label} label={label} val={val} />)}
        </div>
      </Section>

      <hr className="gw-divider" />

      {/* 3. Segments & recommendations */}
      <Section title="3 — SEGMENTS & RECOMMENDATIONS" testid="report-segments">
        <Row label="Primary segment" value={fmt(data.primary_segment)} />
        <Row label="Secondary segment" value={fmt(data.secondary_segment)} />
        <Row label="Recommended onboarding path" value={fmt(data.recommended_onboarding_path)} />
        <Row label="Recommended sales action" value={fmt(data.recommended_sales_action)} />
        <Row label="Recommended AI experience" value={fmt(data.recommended_ai_experience)} />
        <Row label="Interpretation" value={recommendationSentence(data.primary_segment)} />
      </Section>

      <hr className="gw-divider" />

      {/* 4. Roadmap priorities */}
      <Section title="4 — ROADMAP PRIORITY SCORES" testid="report-roadmap">
        <div className="flex flex-col gap-3">
          {roadmapEntries.map(([key, val]) => (
            <div key={key} className="flex flex-col gap-1.5" data-testid={`roadmap-bar-${key}`}>
              <div className="flex items-center justify-between">
                <span className="gw-small" style={{ color: "var(--text-primary)" }}>
                  {ROADMAP_LABELS[key] || humanizeKey(key)}
                </span>
                <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>{val}</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: "var(--page-secondary)", border: "1px solid var(--border-card)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.max(0, (val / maxRoadmap) * 100)}%`, background: "var(--text-primary)", transition: "width 400ms ease" }} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <hr className="gw-divider" />

      {/* 5. Estimated systems cost */}
      <Section title="5 — ESTIMATED SYSTEMS COST (ADMIN ONLY)" testid="report-cost">
        <p className="gw-small" style={{ color: "var(--text-secondary)" }}>
          Estimated from selected tools and company profile. Not user-confirmed.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <ScoreCard label="Monthly range" val={range(data.estimated_monthly_systems_cost_low, data.estimated_monthly_systems_cost_high)} />
          <ScoreCard label="Annual range" val={range(data.estimated_annual_systems_cost_low, data.estimated_annual_systems_cost_high)} />
          <ScoreCard label="Monthly midpoint" val={money(data.estimated_monthly_systems_cost_midpoint)} />
          <ScoreCard label="Annual midpoint" val={money(data.estimated_annual_systems_cost_midpoint)} />
          <ScoreCard label="Cost band" val={fmt(data.estimated_systems_cost_band)} />
          <ScoreCard label="Confidence" val={fmt(data.estimated_systems_cost_confidence)} />
          <ScoreCard label="Replaceable / mo" val={range(data.estimated_replaceable_monthly_cost_low, data.estimated_replaceable_monthly_cost_high)} />
          <ScoreCard label="Integrate-not-replace / mo" val={range(data.estimated_integrate_not_replace_monthly_cost_low, data.estimated_integrate_not_replace_monthly_cost_high)} />
        </div>

        {breakdown.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="report-cost-table">
              <thead>
                <tr style={{ textAlign: "left", color: "var(--text-secondary)" }}>
                  {["Tool", "Category", "Monthly", "Annual", "Replace / integrate", "Confidence", "Notes"].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b, i) => (
                  <tr key={`${b.tool}-${i}`} style={{ color: "var(--text-primary)" }}>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)" }}>{b.tool}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)" }}>{humanizeKey(b.category)}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)", whiteSpace: "nowrap" }}>{range(b.estimated_monthly_low, b.estimated_monthly_high)}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)", whiteSpace: "nowrap" }}>{range(b.estimated_annual_low, b.estimated_annual_high)}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)" }}>{humanizeKey(b.replace_or_integrate)}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)" }}>{humanizeKey(b.confidence)}</td>
                    <td style={{ padding: "8px 10px", borderBottom: "1px solid var(--border-card)", color: "var(--text-secondary)" }}>{b.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Row label="Pricing interpretation" value={data.pricing_interpretation || "—"} />
        {notes.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="gw-small" style={{ color: "var(--text-secondary)" }}>Notes</span>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {notes.map((n, i) => (
                <li key={i} className="gw-body" style={{ color: "var(--text-primary)" }}>{n}</li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <hr className="gw-divider" />

      {/* 6. Raw answers */}
      <Section title="6 — RAW ANSWERS" testid="report-answers">
        <div className="flex flex-col gap-3">
          {QUESTIONS.map((q) => {
            const otherVal = q.otherField ? data[q.otherField] : "";
            return (
              <div key={q.id} className="flex flex-col gap-1">
                <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>{q.question}</span>
                <span className="gw-body" style={{ color: "var(--text-primary)" }}>
                  {renderAnswer(data[q.id])}{otherVal ? ` — ${otherVal}` : ""}
                </span>
              </div>
            );
          })}
          {data.additional_context && data.additional_context.trim() && (
            <div className="flex flex-col gap-1">
              <span className="gw-caption" style={{ color: "var(--text-secondary)" }}>Additional context</span>
              <span className="gw-body" style={{ color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>
                {data.additional_context}
              </span>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
